// Modules
import React, { useState, useEffect, useReducer } from 'react';
import queryString from 'query-string';

// API
import {
  postTransactionReceipt,
  getPaymentLinkData,
  getTransactionPaymentMethod,
} from 'utils/api/transactionPublicApi';
import { getPrivacyPolicy } from 'utils/api/servicesApi';
import { customerProcessTransaction } from 'utils/api/payfabricApi';
import TransactionStatus from 'constants/TransactionStatus';

// Helpers
import {
  getDecimalTotalStr,
  getCardErrorMessage,
  displayTypes,
  displayInitState,
  displayReducer,
} from './paymentLinkHelpers';

// Styles
import styles from './PaymentLink.module.scss';

// Components
import { getPaymentButtonObjects } from 'utils/react-library/utils/paymentHelpers';
import Layout from 'utils/react-library/Layout';
import PracticeBanner from 'utils/react-library/PracticeBanner';
import FlavorText from 'utils/react-library/FlavorText';
import PoweredByPayHero from 'utils/react-library/PoweredByPayHero';
import OrderComplete from 'utils/react-library/OrderComplete';
import ReceiptButtons from 'utils/react-library/ReceiptButtons';
import SuccessHeader from 'utils/react-library/SuccessHeader';

import Loading from 'components/Common/Loading';
import PaymentLinkErrors from './PaymentLinkErrors';
import PaymentLinkMainContent from './PaymentLinkMainContent';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import PaymentMethod from 'constants/PaymentMethod';

function setInitialTransactionScreen(dispatchDisplay, transaction, statusCode) {
  switch (transaction?.status) {
    case TransactionStatus.succeeded:
      dispatchDisplay({ type: displayTypes?.alreadyCompleted });
      break;

    case TransactionStatus.expired:
      dispatchDisplay({ type: displayTypes?.transactionExpired });
      break;

    default:
      statusCode === 206
        ? dispatchDisplay({ type: displayTypes?.partialData })
        : dispatchDisplay({ type: displayTypes?.finishLoading });
      break;
  }
}

async function getTransactionData(args) {
  const {
    queryParams,
    dispatchDisplay,
    setTotal,
    setPaymentButtons,
    setPaymentType,
    setTransaction,
    setPractice,
    setPaymentMethod,
  } = args;

  try {
    /**
     * Contains both transaction and practice data
     * Hence you only need one HTTP request
     */
    const response = await getPaymentLinkData(queryParams);

    const data = response?.data ?? response;

    if (!data) throw new Error('Something went wrong getting the data');

    const { transaction, practice } = data;

    const statusCode = response?.status ?? response?.statusCode ?? 400;

    // If server responds with 200 we have the full data
    // If server responds with 206 we have partial data
    // For example, a transaction that was already completed would return partial data
    if (statusCode !== 200 && statusCode !== 206)
      throw new Error('Something went wrong getting the transaction');

    if (statusCode === 200) {
      const totalStr = getDecimalTotalStr(transaction?.charge?.amount);
      const paymentMethodId = transaction?.petData?.owner?.paymentMethodId;
      const paymentMethod = paymentMethodId
        ? await getTransactionPaymentMethod(queryParams, paymentMethodId)
        : null;

      if (paymentMethodId) {
        setPaymentMethod(paymentMethod);
        setPaymentType('savedCard');
      }

      const paymentButtons = getPaymentButtonObjects(
        totalStr,
        false,
        paymentMethod
      );

      setTotal(totalStr);
      setPaymentButtons(paymentButtons);
    }

    setTransaction(transaction);
    setPractice(practice);
    setInitialTransactionScreen(dispatchDisplay, transaction, statusCode);
  } catch (err) {
    console.log(err);
    dispatchDisplay({ type: displayTypes?.showDataFailed });
  }
}

/**
 * ==================
 * Exported component
 * ==================
 */
function PaymentLink(props) {
  const { location } = props;

  const [loadingMessage, setLoadingMessage] = useState('Loading transaction');
  const [errorMessage, setErrorMessage] = useState(getCardErrorMessage());
  const [paymentButtons, setPaymentButtons] = useState([]);
  const [paymentType, setPaymentType] = useState('full');
  const [total, setTotal] = useState('');
  const [receiptInfo, setReceiptInfo] = useState({});
  const [receiptSentTo, setReceiptSentTo] = useState('');
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cardType, setCardType] = useState('');
  const [lastFourDigits, setLastFourDigits] = useState('');
  const [display, dispatchDisplay] = useReducer(
    displayReducer,
    displayInitState
  );
  const [queryParams, setQueryParams] = useState('');
  const [redirectUrls, setRedirectUrls] = useState({});
  const [transaction, setTransaction] = useState({});
  const [transactionId, setTransactionId] = useState('');
  const [practice, setPractice] = useState({});

  const showBanner =
    Object.values(display).every((val) => !val) ||
    display?.showForm ||
    display?.showReceiptButtons ||
    display?.finished ||
    display?.alreadyCompleted ||
    display?.partialData ||
    display?.transactionExpired;

  const handlePaymentButtonClick = (buttonType) => {
    setPaymentType(buttonType);
  };

  const handlePayBySavedCard = async () => {
    try {
      setLoadingMessage('Processing payment');
      dispatchDisplay({ type: displayTypes?.loading });

      await customerProcessTransaction({
        transactionId,
        customerId: transaction.petData.owner.customerId,
        creditCardId: paymentMethod.payment_method_token,
      });
      handlePayFabricSuccess();
    } catch (e) {
      console.log(e);
      dispatchDisplay({ type: displayTypes?.showError });
      setErrorMessage('There was an issue with PayFabric.');
    }
  };

  const handlePayFabricSuccess = () => {
    dispatchDisplay({ type: displayTypes?.showReceiptButtons });
  };

  const handlePayFabricError = (payFabricErrorMessage) => {
    dispatchDisplay({ type: displayTypes?.showError });
    setErrorMessage(payFabricErrorMessage);
  };

  const handleClosePaymentForm = (e) => {
    e.preventDefault();
    dispatchDisplay({ type: displayTypes?.hideForm });
  };

  const handleReceiptSubmit = (receiptObj) => {
    setLoadingMessage(
      receiptObj?.type === 'none' ? 'Processing' : 'Sending receipt'
    );
    dispatchDisplay({ type: displayTypes?.sendingReceipt });

    async function sendReceipt() {
      try {
        setReceiptSentTo(receiptObj.value);

        await postTransactionReceipt(
          transaction?.charge?.id,
          receiptObj.type,
          receiptObj.value
        );

        setReceiptInfo(receiptObj);

        dispatchDisplay({ type: displayTypes?.finished });
      } catch (err) {
        console.log(err);

        setErrorMessage(`Receipt could not be sent to: ${receiptObj.value}`);

        dispatchDisplay({ type: displayTypes?.receiptFailed });
      }
    }

    sendReceipt();
  };

  const handleReceiptRetry = () => {
    dispatchDisplay({ type: displayTypes?.showReceiptButtons });
  };

  const handleReceiptCancel = () => {
    async function noReceipt() {
      try {
        await postTransactionReceipt(transaction?.charge?.id, 'no_thanks', '');
      } catch (err) {
        console.log(err);
      } finally {
        dispatchDisplay({ type: displayTypes?.finished });
      }
    }

    noReceipt();
  };

  useEffect(() => {
    const urlPath = location?.pathname;
    const parsed = queryString.parse(location.search);
    /**
     * Used to be for SplitIt parameters to not propogate
     */
    const queryParams = `?transactionId=${parsed.transactionId}&key=${parsed.key}`;
    setQueryParams(queryParams);
    setTransactionId(parsed.transactionId);

    const initPrivacyPolicy = async () => {
      const url = await getPrivacyPolicy();
      setPrivacyPolicyUrl(url);
    };

    initPrivacyPolicy();

    const transactionDataArgs = {
      location,
      queryParams,
      dispatchDisplay,
      setTotal,
      setPaymentButtons,
      setPaymentType,
      setTransaction,
      setPractice,
      setRedirectUrls,
      setPaymentMethod,
    };

    /**
     * This used to handle the splitit redirects
     */
    switch (urlPath) {
      case '/pay':
        getTransactionData(transactionDataArgs);
        break;

      default:
        dispatchDisplay({ type: displayTypes?.show404Error });
        break;
    }
  }, [location]);

  return (
    <div className={styles.container}>
      <Layout>
        {display?.loading ? <Loading message={loadingMessage} /> : null}

        {showBanner && (
          <PracticeBanner
            imgUrl={practice?.coreData?.logoUrl}
            date={
              transaction?.charge
                ? `${new Date(transaction?.charge?.created).toLocaleString(
                    'default',
                    {
                      month: 'short',
                    }
                  )} ${transaction?.charge?.day}, ${transaction?.charge?.year}`
                : ''
            }
            name={practice?.name}
            street={
              practice?.coreData?.address1
                ? `${practice?.coreData?.address1}${
                    practice?.coreData?.address2
                      ? `, ${practice?.coreData?.address2}`
                      : ''
                  }`
                : ''
            }
            city={practice?.coreData?.city ?? ''}
            state={practice?.coreData?.state ?? ''}
            zip={practice?.coreData?.zip ?? ''}
            phone={practice?.phone ? practice?.phone.toString() : ''}
          />
        )}

        <PaymentLinkMainContent
          {...{
            total,
            transaction,
            practice,
            display,
            dispatchDisplay,
            paymentType,
            displayTypes,
            paymentButtons,
            handlePaymentButtonClick,
            handleClosePaymentForm,
            handlePayFabricSuccess,
            handlePayFabricError,
            redirectUrls,
            setCardType,
            setLastFourDigits,
            queryParams,
            handlePayBySavedCard,
          }}
        />

        {display?.showReceiptButtons ? (
          <>
            <SuccessHeader total={total} />
            <ReceiptButtons
              callback={handleReceiptSubmit}
              phone={transaction?.petData?.owner?.phone.replace?.('+1', '')}
              email={transaction?.petData?.owner?.contactEmail}
              cardType={cardType}
              lastFourDigits={lastFourDigits}
              defaultSelected={
                transaction?.charge?.method === PaymentMethod.EmailLink
                  ? 'email'
                  : 'text_message'
              }
            />
          </>
        ) : null}

        {display?.finished && receiptInfo?.type && receiptInfo?.value ? (
          <OrderComplete
            receiptType={receiptInfo?.type}
            sentTo={receiptInfo?.value}
          />
        ) : display?.finished ? (
          <OrderComplete />
        ) : null}

        <PaymentLinkErrors
          {...{
            display,
            dispatchDisplay,
            displayTypes,
            errorMessage,
            total,
            practice,
            handleReceiptCancel,
            handleReceiptRetry,
            receiptSentTo,
          }}
        />

        {showBanner && practice?.paymentLinkCustomMessage ? (
          <FlavorText text={`"${practice?.paymentLinkCustomMessage}"`} />
        ) : null}
      </Layout>

      <PoweredByPayHero />

      <footer className={styles.footer}>
        <button
          className={`btn btn-link ${styles.footerBtn}`}
          onClick={() => setShowPrivacyPolicy((prev) => !prev)}
        >
          Privacy Policy
        </button>

        <small className={styles.copyright}>
          COPYRIGHT {new Date(Date.now()).getFullYear()} &#169; VetHero All
          rights reserved
        </small>
      </footer>

      <PrivacyPolicyModal
        show={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy((prev) => !prev)}
        link={privacyPolicyUrl}
      />
    </div>
  );
}

export default PaymentLink;
